import React, { useContext, useEffect, useRef, useState } from "react";
import { Button, Dropdown, Form, OverlayTrigger } from "react-bootstrap";
import { withTranslation } from "react-i18next";
import { BiDownload, BiPencil, BiTransfer, } from "react-icons/bi";
import { BsFileEarmarkZip, BsFileEarmarkSpreadsheet, BsLayoutSplit, BsFilter, BsEye } from "react-icons/bs";
import { toast } from "react-toastify";
import { LoaderContext } from "../../../../App";
import { DATA_VIEW_IMPORT_POLLING_INTERVAL } from "../../../../core/constants/common";
import KPIService from "../../../../core/service/KPIService";
import '../DataView.scss';
import Utils from "../../../../core/utilities/Utils";
import { Months, startYear } from "../../../../core/constants/common";
import { AiOutlineApartment } from "react-icons/ai";
import { useNavigate } from "react-router-dom";
import { LS_AUTHORITY_KEY, LS_COMPANY_INFO_KEY, LS_REGULATION_SELECTED_KEY, userPermissionRoles } from "../../../../core/constants/common";
import moment from "moment";
import { FiAlertTriangle } from "react-icons/fi";
import useHasPermission from "../../../../core/hooks/useHasPermission";
import { getFYAdjustedFilter } from "./DataViewNew";
let importStatusPoller;

/**
 * Period label for the header. For FY companies, full fiscal-year wording only when periodType is
 * ANNUAL/YEARLY (matches DataViewNew#getFYAdjustedFilter). QUARTERLY/CUSTOM use actual periodFrom/periodTo.
 */
export const computePeriodStatusDisplay = (filter, optionsMonth, isConsolidatedView, currentYear, companyInfo = {}) => {
    if (filter?.periodFrom == null) return null;
    const isFY = (companyInfo?.frequencyType ?? companyInfo?.dataFrequencyType) === 'FY';
    const normalizedPeriodType = String(filter?.periodType || '').toUpperCase();
    const isYearlyPeriod = normalizedPeriodType === 'ANNUAL' || normalizedPeriodType === 'YEARLY';
    if (isFY && isYearlyPeriod) {
        const yearFromReporting = Number(filter?.reportingPeriod?.split('-')?.[0]);
        const yearFromPeriodFrom = new Date(filter.periodFrom).getFullYear();
        const reportingYear =
            (Number.isFinite(yearFromReporting) && yearFromReporting > 0
                ? yearFromReporting
                : yearFromPeriodFrom) || currentYear;
        const fyStartMonth = Utils.getMonthNumber(companyInfo?.financialYearStart || 'APRIL');
        const fyEndMonth = Utils.getMonthNumber(companyInfo?.financialYearEnd || 'MARCH');
        const fyStartYear = reportingYear - 1;
        const fyEndYear = reportingYear;
        const idxStart = parseInt(fyStartMonth, 10) - 1;
        const idxEnd = parseInt(fyEndMonth, 10) - 1;
        const fromStr = `${optionsMonth[idxStart]?.label} ${fyStartYear}`;
        const toStr = `${optionsMonth[idxEnd]?.label} ${fyEndYear}`;
        const showRange = isConsolidatedView
            ? (filter.periodTo != null && filter.periodType !== 'MONTHLY')
            : (filter.periodTo != null);
        return showRange ? `${fromStr} to ${toStr}` : fromStr;
    }
    const fromStr = `${optionsMonth[new Date(filter.periodFrom).getMonth()].label} ${new Date(filter.periodFrom).getFullYear()}`;
    const showRange = isConsolidatedView
        ? (filter.periodTo != null && filter.periodType !== 'MONTHLY')
        : (filter.periodTo != null);
    if (showRange) {
        return `${fromStr} to ${optionsMonth[new Date(filter.periodTo).getMonth()].label} ${new Date(filter.periodTo).getFullYear()}`;
    }
    return fromStr;
};

const DataViewHeaderNew = ({
    t,
    filter,
    regulationId,
    view,
    viewType,
    equationExecutionInProgress = false,
    dataStale = false,
    onReloadStaleData,
    onFilterClick,
    onViewChange,
    onViewTypeChange,
    onColHideShow,
    filterChange,
    onExitEdit,
    onSaveEdit,
}) => {

    const ref = useRef();
    /** Previous `filter.periodType` to detect radio switches without resetting on first mount. */
    const prevPeriodTypeRef = useRef(null);
    const { setLoaderDisplay } = useContext(LoaderContext);
    const [importInProgress, setImportInProgress] = useState(false);
    const [frequencymonth, setFrequencyMonth] = useState(String(new Date(filter.reportingPeriod).getMonth() + 1).padStart(2, '0'));
    const [annualyear, setAnnualYear] = useState(new Date().getFullYear());
    const optionsMonth = Months({ t });
    const currentYear = new Date().getFullYear();
    const companyInfo = JSON.parse(localStorage.getItem(LS_COMPANY_INFO_KEY) || '{}');
    const currentMonth = new Date().getMonth() + 1;
    const [yearOptions, setYearOptions] = useState([]);
    const [colHideShow, setColHideShow] = useState({
        codes: true,
        uom: true,
        userInput: true,
        inUOM: false,
        showTotalReportedValue: false
    });
    const navigate = useNavigate();
    const { hasDMPeriodicUpdatePermission } = useHasPermission();

    useEffect(() => {
        fetchImportStatus(true);
        let years = [];
        for (let i = currentYear; i >= startYear; i--) {
            years.push(
                <option value={i} key={i}>{i}</option>
            );
        }
        setYearOptions(years);
        return () => {
            // On unmounting
            clearImportStatusPoller();
        };
    }, []);

    useEffect(() => {
        onColHideShow(colHideShow);
    }, [colHideShow]);

    /**
     * Quantitative: Monthly — Edit dropdown matches filter Period (`periodFrom`).
     * Yearly / Quarterly — on switching to those radios, Edit dropdown shows **current** calendar year/month
     * (legacy behavior); skip on initial mount so default state stays as before.
     */
    useEffect(() => {
        if (filter.showQualitative) return;
        const pt = String(filter.periodType || '').toUpperCase();
        const prevRaw = prevPeriodTypeRef.current;
        prevPeriodTypeRef.current = filter.periodType;

        if (pt === 'MONTHLY') {
            const raw = filter.periodFrom || filter.reportingPeriod;
            if (!raw || typeof raw !== 'string') return;
            const ymd = raw.split('T')[0].split('-');
            if (ymd.length < 2) return;
            const y = Number(ymd[0]);
            const m = Number(ymd[1]);
            if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) return;
            setAnnualYear(y);
            setFrequencyMonth(String(m).padStart(2, '0'));
            return;
        }

        if (
            pt === 'ANNUAL' ||
            pt === 'YEARLY' ||
            pt === 'QUARTERLY'
        ) {
            if (prevRaw === null) return;
            const prevNorm = String(prevRaw).toUpperCase();
            if (prevNorm === pt) return;
            const cy = new Date().getFullYear();
            const cm = new Date().getMonth() + 1;
            setAnnualYear(cy);
            setFrequencyMonth(String(cm).padStart(2, '0'));
        }
    }, [
        filter.periodFrom,
        filter.reportingPeriod,
        filter.periodType,
        filter.showQualitative,
    ]);

    const onExport = () => {
        setLoaderDisplay(true);
        const apiFilter = getFYAdjustedFilter(filter, companyInfo, currentYear);
        KPIService.exportMetricDataview(regulationId, apiFilter.reportingPeriod, apiFilter.businessUnitIds, apiFilter.topicIds, apiFilter.categoryType, apiFilter, colHideShow.showTotalReportedValue)
            .then((response) => {
                setLoaderDisplay(false);
                let blob = new Blob([response.data], { type: 'application/octet-stream' });
                const fileURL = window.URL.createObjectURL(blob);
                let link = document.createElement('a');
                link.href = fileURL;
                link.download = `Periodic Data.xlsx`;
                link.click();
                link.remove();
            })
            .catch((error) => {
                setLoaderDisplay(false);
                toast.error(error.message, {
                    position: toast.POSITION.TOP_RIGHT,
                });
            });
    };

    const onFileSelect = (e) => {
        e.preventDefault();
        const fileUploaded = e.target.files[0];
        importFile(fileUploaded);
    };

    const importFile = (file) => {
        const formData = new FormData();
        formData.append("file", file);
        setLoaderDisplay(true);
        KPIService.uploadFile(formData, regulationId, filter.reportingPeriod)
            .then((response) => {
                toast.success(response.data, {
                    position: toast.POSITION.TOP_RIGHT,
                });
                setImportInProgress(true);
                fetchImportStatus(true);
            })
            .catch((error) => {
                setLoaderDisplay(false);
            })
            .finally(() => {
                setLoaderDisplay(false);
            });
    };

    const fetchImportStatus = (startPoller = false) => {
        KPIService.getImportStatus().then(res => {
            if (res.data.data) {
                setImportInProgress(true);
                if (!startPoller) return;

                startImportStatusPoller();
            } else {
                clearImportStatusPoller();
            }
        }).catch(err => {
            clearImportStatusPoller();
        });
    };


    const startImportStatusPoller = () => {
        importStatusPoller = setTimeout(function request() {
            fetchImportStatus();
            importStatusPoller = setTimeout(request, DATA_VIEW_IMPORT_POLLING_INTERVAL);
        }, DATA_VIEW_IMPORT_POLLING_INTERVAL);
    };

    const clearImportStatusPoller = () => {
        setImportInProgress(false);
        if (!importStatusPoller) return;

        clearTimeout(importStatusPoller);
    };

    const [isActive, setIsActive] = useState(false);

    const handleClick = () => {
        setIsActive(!isActive); // Toggle the active state
        onFilterClick(); // Call the provided function
    };

    const onDownloadDocuments = () => {
        const date = new Date(filter.reportingPeriod);
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        let categoryType = '';
        if (filter.categoryType === 0) {
            categoryType = 'ENVIRONMENTAL';
        } else if (filter.categoryType === 1) {
            categoryType = 'SOCIAL';
        } else if (filter.categoryType === 2) {
            categoryType = 'GOVERNANCE';
        }
        setLoaderDisplay(true);
        KPIService.exportMetricDataAttachments(regulationId, filter.reportingPeriod, filter.businessUnitIds, filter.topicIds, categoryType)
            .then((response) => {
                setLoaderDisplay(false);
                let blob = new Blob([response.data], { type: 'application/octet-stream' });
                const fileURL = window.URL.createObjectURL(blob);
                let link = document.createElement('a');
                link.href = fileURL;
                link.download = `attachments-${month}-${year}.zip`;
                link.click();
                link.remove();
            })
            .catch(async (error) => {
                setLoaderDisplay(false);
                const blobToText = await error.response.data.text();
                const textToJson = JSON.parse(blobToText);
                toast.error(textToJson.message, {
                    position: toast.POSITION.TOP_RIGHT,
                });
            });
    };

    const gotoCompare = () => {
        navigate({
            pathname: "/company_admin/compare",
        });
    };

    const gotoAnomalyDashboard = () => {
        const apiFilter = getFYAdjustedFilter(filter, companyInfo, currentYear);
        const periodFrom = apiFilter.periodFrom
            ? moment(apiFilter.periodFrom).format("YYYY-MM-DD")
            : moment(apiFilter.reportingPeriod).format("YYYY-MM-DD");
        const periodTo = apiFilter.periodTo
            ? moment(apiFilter.periodTo).format("YYYY-MM-DD")
            : moment(periodFrom).endOf("month").format("YYYY-MM-DD");

        navigate("/company_admin/anomalies/dashboard", {
            state: {
                regulationId: regulationId || localStorage.getItem(LS_REGULATION_SELECTED_KEY) || "",
                periodFrom,
                periodTo,
            },
        });
    };

    const getPeriodStatusDisplay = (isConsolidatedView) =>
        computePeriodStatusDisplay(filter, optionsMonth, isConsolidatedView, currentYear, companyInfo);

    return (
        <div className="header-actions">

            <>
                {
                    equationExecutionInProgress &&
                    <span className="Filter_status alert alert-warning py-1 px-2 mb-0 d-inline-flex align-items-center">
                        <div className="spinner-border spinner-border-sm me-2 text-warning" role="status" aria-hidden="true"></div>
                        {t("EquationExecutionInProgress.label")}
                    </span>
                }
                {
                    !equationExecutionInProgress && dataStale &&
                    <span className="Filter_status alert alert-info py-1 px-2 mb-0 d-inline-flex align-items-center">
                        {t("DataValuesChangedReload.label")}
                        <Button
                            variant="link"
                            className="alert-link p-0 ms-2"
                            onClick={onReloadStaleData}>
                            {t("ReloadToolTip.label")}
                        </Button>
                    </span>
                }
                {
                    filter.showQuantitative &&
                    <span className="Filter_status"><h6>{t("Type.label")}: </h6>{t("Quantitative.label")}</span>
                }
                {
                    filter.showQualitative &&
                    <span className="Filter_status"><h6>{t("Type.label")}: </h6>{t("Qualitative.label")}</span>
                }

            </>

            {
                view === 'CONSOLIDATED_VIEW' &&
                <>
                    {(filter.periodFrom != null) &&
                        <div>
                            <span className="Filter_status"><h6>{t("Period.label")}: </h6>
                                {getPeriodStatusDisplay(true)}
                            </span>
                        </div>
                    }

                </>
            }
            {
                view === 'EDIT_VIEW' &&
                <>
                    {(filter.periodFrom != null) &&
                        <div>
                            <span className="Filter_status"><h6>{t("Period.label")}: </h6>
                                {getPeriodStatusDisplay(false)}
                            </span>
                        </div>
                    }
                </>
            }

            {
                (view == 'EDIT_VIEW' && filter.showQuantitative) &&
                <>
                    <Button variant="secondary" className="px-3" onClick={() => onExitEdit('cancel')}>{t("Cancel.label")}</Button>
                    <Button variant="primary" className="px-3" onClick={() => onSaveEdit('save')}>{t("Save.label")}</Button>
                </>
            }

            {
                (view == 'EDIT_VIEW' && filter.showQualitative) &&
                <>
                    <Button variant="secondary" className="px-3" onClick={() => { onViewChange('CONSOLIDATED_VIEW') }}>{t("Cancel.label")}</Button>
                </>
            }

            {
                view !== 'EDIT_VIEW' && !filter.showQualitative &&
                <OverlayTrigger
                    placement="top"
                    overlay={Utils.renderTooltip(t("Edit.label"))}>
                    <Dropdown>
                        <Dropdown.Toggle
                            variant="headbtn"
                            id="dropdown-basic"
                            hidden={!hasDMPeriodicUpdatePermission()}
                        >
                            <BiPencil />
                        </Dropdown.Toggle>

                        <Dropdown.Menu className="Edit_dropdown p-2">
                            <div className="row m-0">
                                <div className="col-md-12 p-2">
                                    <h6 className="mb-0">{t("Edit.label")}</h6>
                                </div>

                                <div className="col-md-5 p-2">
                                    <label className="mb-1">{t("Year.label")}</label>
                                    <select className="form-select "
                                        onChange={(e) => {
                                            setAnnualYear(e.target.value);
                                        }}
                                        value={annualyear}>
                                        {yearOptions}
                                    </select>
                                </div>

                                <div className="col-md-7 p-2">
                                    <label className="mb-1">{t("Month.label")}</label>
                                    <select
                                        className="form-select "
                                        value={frequencymonth}
                                        onChange={(e) => {
                                            setFrequencyMonth(e.target.value);
                                        }}>
                                        {
                                            optionsMonth?.map((items) => (
                                                <option value={items.value} key={items.value}
                                                    disabled={regulationId && annualyear == currentYear && items.value > currentMonth ? true : false}>
                                                    {items.label}
                                                </option>
                                            ))
                                        }
                                    </select>
                                </div>

                                <div className="col-md-12 p-2 text-center">
                                    <Button variant="primary"
                                        className="sbmBtn px-3"
                                        onClick={() => {
                                            filterChange(annualyear, frequencymonth);
                                            onViewChange('EDIT_VIEW');
                                        }}>
                                        {t("Proceed.label")}
                                    </Button>
                                    {/* <Button variant="secondary"
                                    className="px-4 mx-2">
                                    {t("Reset.label")}
                                </Button> */}
                                </div>
                            </div>
                        </Dropdown.Menu>
                    </Dropdown>
                </OverlayTrigger>
            }

            <OverlayTrigger
                placement="top"
                overlay={Utils.renderTooltip(
                    t("Filter.label")
                )}>
                <button
                    // ${isActive ? 'btn-headbtn' : 'btn-headbtn active'}
                    className="btn btn-headbtn"
                    onClick={handleClick}
                >
                    <BsFilter />
                </button>
            </OverlayTrigger>

            <OverlayTrigger
                overlay={Utils.renderTooltip(
                    t("ShowColumns.label")
                )}>
                <Dropdown>
                    <Dropdown.Toggle variant="headbtn" id="dropdown-basic">
                        <BsLayoutSplit />
                    </Dropdown.Toggle>

                    <Dropdown.Menu className="CustomizeColumns_drop p-2">
                        <div className="p-2">
                            <h6 className="mb-0">{t("ShowColumns.label")}</h6>
                        </div>
                        <ul>
                            <li>
                                <Form.Check
                                    inline
                                    name="group1"
                                    label={t("Code.label")}
                                    type="checkbox"
                                    id="inline-checkbox-1"
                                    checked={colHideShow.codes}
                                    onChange={() => setColHideShow({ ...colHideShow, codes: !colHideShow.codes })}
                                />
                            </li>
                            {filter.showQuantitative &&
                                <li>
                                    <Form.Check
                                        inline
                                        name="group1"
                                        label={t("Uom.label")}
                                        type="checkbox"
                                        id="inline-checkbox-2"
                                        checked={colHideShow.uom}
                                        onChange={() => setColHideShow({ ...colHideShow, uom: !colHideShow.uom })}
                                    />
                                </li>
                            }
                            {filter.showQuantitative &&
                                <li>
                                    <Form.Check
                                        inline
                                        name="group1"
                                        label={t("DataViewNew.inUOM.label")}
                                        type="checkbox"
                                        id="inline-checkbox-3"
                                        checked={colHideShow.inUOM}
                                        onChange={() => {
                                            const newInUOM = !colHideShow.inUOM;
                                            // Ensure at least one of inUOM or userInput is enabled
                                            if (!newInUOM && !colHideShow.userInput) {
                                                // Cannot disable both, keep current state
                                                return;
                                            }
                                            setColHideShow({ ...colHideShow, inUOM: newInUOM });
                                        }}
                                    />
                                </li>
                            }
                            {filter.showQuantitative &&
                                <li>
                                    <Form.Check
                                        inline
                                        name="group1"
                                        label={t("DataViewNew.userInput.label")}
                                        type="checkbox"
                                        id="inline-checkbox-4"
                                        checked={colHideShow.userInput}
                                        onChange={() => {
                                            const newUserInput = !colHideShow.userInput;
                                            // Ensure at least one of inUOM or userInput is enabled
                                            if (!newUserInput && !colHideShow.inUOM) {
                                                // Cannot disable both, keep current state
                                                return;
                                            }
                                            setColHideShow({ ...colHideShow, userInput: newUserInput });
                                        }}
                                    />
                                </li>
                            }
                            {filter.showQuantitative &&
                                <li>
                                    <Form.Check
                                        inline
                                        name="group1"
                                        label={t("DataViewNew.showTotalReportedValue.label")}
                                        type="checkbox"
                                        id="inline-checkbox-5"
                                        checked={colHideShow.showTotalReportedValue}
                                        onChange={() => setColHideShow({ ...colHideShow, showTotalReportedValue: !colHideShow.showTotalReportedValue })}
                                    />
                                </li>
                            }
                        </ul>
                    </Dropdown.Menu>
                </Dropdown>
            </OverlayTrigger>

            <OverlayTrigger
                placement="top"
                overlay={Utils.renderTooltip(
                    t("Download.label")
                )}>
                <Dropdown>
                    <Dropdown.Toggle variant="headbtn" id="dropdown-basic">
                        <BiDownload />
                    </Dropdown.Toggle>

                    <Dropdown.Menu>
                        <OverlayTrigger
                            placement="top"
                            overlay={Utils.renderTooltip(
                                t("DownloadDocuments.label")
                            )}>
                            <Dropdown.Item onClick={() => onDownloadDocuments()}>
                                <BsFileEarmarkZip className="me-1" /> {t("Documents&File.label")}
                            </Dropdown.Item>
                        </OverlayTrigger>
                        <OverlayTrigger
                            placement="top"
                            overlay={Utils.renderTooltip(
                                t("DownloadExcel.label")
                            )}>
                            <Dropdown.Item onClick={() => onExport()}>
                                <BsFileEarmarkSpreadsheet className="me-1" /> {t("Spreadsheet.label")}
                            </Dropdown.Item>
                        </OverlayTrigger>
                    </Dropdown.Menu>
                </Dropdown>
            </OverlayTrigger>

            {/* {view !== "EDIT_VIEW" && (
                <OverlayTrigger
                    placement="top"
                    overlay={Utils.renderTooltip(t("Anomaly.label"))}>
                    <Button
                        variant="headbtn"
                        onClick={gotoAnomalyDashboard}
                        className="sbmBtn anomaly-nav-btn"
                    >
                        <FiAlertTriangle className="me-1" />
                        {t("Anomaly.label")}
                    </Button>
                </OverlayTrigger>
            )}  */}

            <OverlayTrigger
                placement="top"
                overlay={Utils.renderTooltip(
                    t("Compare.label")
                )}>
                <Button variant="headbtn"
                    onClick={() => { gotoCompare() }}
                    className="sbmBtn compareBtn">
                    <BiTransfer />
                </Button>
            </OverlayTrigger>
        </div >
    );
};

export default withTranslation()(DataViewHeaderNew);
